import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, FormControl, InputGroup } from 'react-bootstrap';
import { Ban, DollarSign, Edit } from 'lucide-react'; // Icons for edit and block actions
import styled from 'styled-components';
import GenericModal from '../../components/GenericModal';
import GenericTable from '../../components/GenericTable';
import { useAuth } from '../../context/AuthContext';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { User } from '../../models/Users';
import { addPoints, getAllUsers, revokePoints, saveUser, toggleUserStatus, updateUser } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Role } from '../../utils/rolesUtil';
import AddUserPage from '../AddUserPage';
import AddPointsPage from './AddPointsPage';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const AddButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 2rem;
  gap: 10px;
`;

const SearchBox = styled(InputGroup)`
  max-width: 300px;
  flex-grow: 1; /* Allow the search box to take up available space */
  margin-right: 15px;
  margin-bottom: 1rem;

  @media (max-width: 576px) {
    margin-right: 0;
    max-width: 100%; /* Ensure full width on small screens */
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionIcon = styled.div`
  cursor: pointer;
  color: #555;
  font-size: 1.5rem;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }
`;

const UsersPage: React.FC = () => {
  const [modalShow, setModalShow] = useState(false);
  const [selectedItem, setSelectedItem] = useState<User>({} as User);
  const [searchQuery, setSearchQuery] = useState('');
  const { role } = useAuth();
  const [modalPointShow, setModalPointShow] = useState({
    action: '', // add / remove
    show: false  // true | false
  });

  const dispatch = useAppDispatch();
  const {showModalStrip} = useModalStrip();
  const rawUsers = useAppSelector((state) => state.entities.userReducer.users);
  const users = useMemo(() => (Array.isArray(rawUsers) ? rawUsers : []), [rawUsers]);

  useEffect(() => {
    dispatch(getAllUsers(0)); // Fetch users if not already available in Redux
  }, [dispatch]);

  // State to store filtered data (customers only)
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // Filter users based on the search query
  const handleSearch = useCallback(() => {
    // Filter data by the search query
    if (searchQuery) {
      setFilteredData(users.filter((item: any) =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchQuery.toLowerCase())
      ));
    } else {
      setFilteredData(users);
    }
  }, [searchQuery, users]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);


  const handleDelete = (user: User) => {
    dispatch(toggleUserStatus(user.id)).unwrap().then(res => {
      getAllUsers(0);
    }).catch(err => {
      showModalStrip('danger', err.message, 5000);
    });
  };


  const handleAddPoints = (user: User, point: any, action: string) => {
    if (action === 'add') {
      dispatch(addPoints({user, point})).unwrap().then((res) => {
        setModalPointShow({
          ...modalPointShow,
          action: '',
          show: false
        });
        
        dispatch(getAllUsers(0))
      }).catch((err: Error) => {
        showModalStrip('danger', err.message, 5000);
      })   
    } else if (action === 'remove') {
      dispatch(revokePoints({user, point})).unwrap().then((res) => {
        setModalPointShow({
          ...modalPointShow,
          action: '',
          show: false
        });
        dispatch(getAllUsers(0))
      }).catch((err: Error) => {
        showModalStrip('danger', err.message, 5000);
      })   
    } else {
      return;
    }
  }
  
  const handleOpenModal = (item: any, mode: string, role: string) => {
    setSelectedItem({
      ...item,
      role: role
    });
    if (mode === 'add') {
      setModalShow(true);
    } else {
      if (item.status === 'INACTIVE') {
        return;
      }
      setModalShow(true);
    }
  };

  const handleOpenPointsModal = (item: User, action: string) => {
    setSelectedItem(item);
    setModalPointShow({
      ...modalPointShow,
      action: action,
      show: true
    });
  };

  const handleSaveUser = (user: User) => {
    if (user.id) {
      dispatch(updateUser(user)).unwrap().then((res) => {
        setModalShow(false);
        dispatch(getAllUsers(0))
      }).catch((err: any) => {
        showModalStrip('danger', err?.message || err, 5000);
      })
    } else {
      dispatch(saveUser(user)).unwrap().then((res) => {
        setModalShow(false);
        dispatch(getAllUsers(0))
      }).catch((err: any) => {
        showModalStrip('danger', err?.message || err, 5000);
      })
    }
  }

  const isInactive = (item: any) => {
    return item?.status === 'INACTIVE';  // Assuming 'status' is a field in the data
  }

  return (
    <Container>
      <PageHeading>Admin View</PageHeading>

      <AddButtonContainer>
        {/* Add New User Button */}
        {role === Role.SuperAdmin && <Button onClick={() => handleOpenModal({}, 'add', Role.Admin)}>Add Admin</Button> }
      </AddButtonContainer>

      <TableWrapper>
        {/* Search Box */}
        <SearchBox>
          <FormControl
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBox>

        <GenericTable
          columns={[
            { header: 'ID', field: 'id' },
            { header: 'Name', field: 'name' },
            { header: 'Phone', field: 'mobile' },
            { header: 'Points', field: 'points', render(item) {
              return item.points? Number(item.points).toFixed(2) : 0;
            } },
            { header: 'Commission(%)', field: 'commissionRate', render(item) {
              return item.commissionRate? Number(item.commissionRate).toFixed(2) : 0;
            } },
            { header: 'Digit View', field: 'canSeeDigitView' },
            { header: 'Actions', field: 'actions' },
          ]}
          data={filteredData.map((item) => ({
            ...item,
            actions: (
              <ActionButtonContainer>
                <ActionIcon onClick={() => handleOpenModal(item,'edit', Role.Customer)}>
                  <Edit />
                </ActionIcon>
                <ActionIcon onClick={() => handleOpenPointsModal(item, 'add')}>
                  <DollarSign color={isInactive(item) ? '#bb2124' : '#0d4a14'}/>
                </ActionIcon>
                <ActionIcon onClick={() => handleOpenPointsModal(item, 'remove')}>
                  <DollarSign color={'#bb2124'}/>
                </ActionIcon>
                <ActionIcon onClick={() => handleDelete(item)}>
                  <Ban color={isInactive(item) ? '#bb2124' : '#0d4a14'}/>
                </ActionIcon>
              </ActionButtonContainer>
            ),
          }))}
          actions={false} // Don't display the generic edit and delete buttons, we are using custom actions
          pagination={true}
        />
      </TableWrapper>

      {modalShow && (
        <GenericModal
          isOpen={modalShow}
          onClose={() => setModalShow(false)}
          node={<AddUserPage data={selectedItem as User} onSave={(user) => handleSaveUser(user)} />}
          label={selectedItem && selectedItem.id ? `Update User - ${selectedItem.role}` : `Add New - ${selectedItem.role}`}
        />
      )}

      {modalPointShow && modalPointShow.show && (
        <GenericModal
          isOpen={modalPointShow.show}
          onClose={() => setModalPointShow({...modalPointShow, show: false})}
          node={<AddPointsPage data={selectedItem as User} action={modalPointShow.action} onSave={(point) => handleAddPoints(selectedItem, point, modalPointShow.action)} />}
          label={modalPointShow.action === 'add' ? `Add Points` : 'Remove Points'}
        />
      )}
    </Container>
  );
};

export default UsersPage;
